/**
 * Utility functions for person image positioning and scaling in the canvas
 */

/**
 * Calculate the default centered position for a person image
 * @param {number} canvasWidth - Width of the canvas container
 * @param {number} canvasHeight - Height of the canvas container
 * @param {number} imageWidth - Natural width of the person image
 * @param {number} imageHeight - Natural height of the person image
 * @param {number} scale - Current scale factor (default: 1.0)
 * @returns {Object} Position object with x and y coordinates
 */
export const getDefaultPosition = (canvasWidth, canvasHeight, imageWidth, imageHeight, scale = 1.0) => {
  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;
  
  // Center horizontally and vertically in the canvas
  const centerX = (canvasWidth - scaledWidth) / 2;
  const centerY = (canvasHeight - scaledHeight) / 2;
  
  return {
    x: Math.max(0, centerX),
    y: Math.max(0, centerY)
  };
};

/**
 * Constrain position to keep image within canvas bounds
 * @param {Object} position - Current position {x, y}
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {number} scale - Current scale factor
 * @returns {Object} Constrained position object
 */
export const constrainPosition = (position, canvasSize, imageSize, scale = 1.0) => {
  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;
  
  // Calculate bounds
  const minX = 0;
  const minY = 0;
  const maxX = Math.max(0, canvasSize.width - scaledWidth);
  const maxY = Math.max(0, canvasSize.height - scaledHeight);
  
  return {
    x: Math.max(minX, Math.min(position.x, maxX)),
    y: Math.max(minY, Math.min(position.y, maxY))
  };
};

/**
 * Calculate optimal scale to fit image within canvas while preserving aspect ratio
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {number} maxScale - Maximum allowed scale (default: 1.0, no upscaling)
 * @param {number} padding - Padding to leave around image (default: 20px)
 * @returns {number} Optimal scale factor
 */
export const getOptimalScale = (canvasSize, imageSize, maxScale = 1.0, padding = 20) => {
  const availableWidth = canvasSize.width - (padding * 2);
  const availableHeight = canvasSize.height - (padding * 2);
  
  // For person images, prioritize height to ensure they're prominently displayed
  // Target: person image should be at least 60% of canvas height
  const targetHeight = canvasSize.height * 0.6;
  const scaleForHeight = targetHeight / imageSize.height;
  
  const scaleX = availableWidth / imageSize.width;
  const scaleY = availableHeight / imageSize.height;
  
  // Use the scale that ensures good visibility while fitting in canvas
  const optimalScale = Math.min(scaleX, scaleY);
  
  // Ensure person image is prominent (at least 60% scale or height-based scale)
  const minScale = Math.max(0.6, scaleForHeight);
  
  // Don't exceed maxScale but ensure good visibility
  return Math.max(minScale, Math.min(optimalScale, maxScale));
};

/**
 * Calculate image bounds after scaling and positioning
 * @param {Object} position - Image position {x, y}
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {number} scale - Scale factor
 * @returns {Object} Bounds object {left, top, right, bottom, width, height}
 */
export const getImageBounds = (position, imageSize, scale = 1.0) => {
  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;
  
  return {
    left: position.x,
    top: position.y,
    right: position.x + scaledWidth,
    bottom: position.y + scaledHeight,
    width: scaledWidth,
    height: scaledHeight
  };
};

/**
 * Check if image fits within canvas bounds
 * @param {Object} position - Image position {x, y}
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {number} scale - Scale factor
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 * @returns {boolean} True if image fits within canvas
 */
export const isImageWithinBounds = (position, imageSize, scale, canvasSize) => {
  const bounds = getImageBounds(position, imageSize, scale);
  
  return (
    bounds.left >= 0 &&
    bounds.top >= 0 &&
    bounds.right <= canvasSize.width &&
    bounds.bottom <= canvasSize.height
  );
};

/**
 * Preserve aspect ratio when scaling
 * @param {Object} originalSize - Original image dimensions {width, height}
 * @param {number} newScale - New scale factor
 * @returns {Object} Scaled dimensions {width, height}
 */
export const preserveAspectRatio = (originalSize, newScale) => {
  return {
    width: originalSize.width * newScale,
    height: originalSize.height * newScale
  };
};

/**
 * Calculate center point of an image
 * @param {Object} position - Image position {x, y}
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {number} scale - Scale factor
 * @returns {Object} Center point {x, y}
 */
export const getImageCenter = (position, imageSize, scale = 1.0) => {
  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;
  
  return {
    x: position.x + (scaledWidth / 2),
    y: position.y + (scaledHeight / 2)
  };
};

/**
 * Calculate position to center image at a specific point
 * @param {Object} centerPoint - Desired center point {x, y}
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {number} scale - Scale factor
 * @returns {Object} Position to achieve centering {x, y}
 */
export const positionAtCenter = (centerPoint, imageSize, scale = 1.0) => {
  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;
  
  return {
    x: centerPoint.x - (scaledWidth / 2),
    y: centerPoint.y - (scaledHeight / 2)
  };
};

/**
 * Calculate snap points for image positioning (edges and center)
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {number} scale - Scale factor
 * @returns {Array} Array of snap points with labels
 */
export const getSnapPoints = (canvasSize, imageSize, scale = 1.0) => {
  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;
  
  return [
    // Center
    {
      position: getDefaultPosition(canvasSize.width, canvasSize.height, imageSize.width, imageSize.height, scale),
      label: 'center',
      type: 'center'
    },
    // Top-left
    {
      position: { x: 0, y: 0 },
      label: 'top-left',
      type: 'corner'
    },
    // Top-right
    {
      position: { x: canvasSize.width - scaledWidth, y: 0 },
      label: 'top-right',
      type: 'corner'
    },
    // Bottom-left
    {
      position: { x: 0, y: canvasSize.height - scaledHeight },
      label: 'bottom-left',
      type: 'corner'
    },
    // Bottom-right
    {
      position: { x: canvasSize.width - scaledWidth, y: canvasSize.height - scaledHeight },
      label: 'bottom-right',
      type: 'corner'
    }
  ].filter(snap => 
    // Only include valid positions (within bounds)
    snap.position.x >= 0 && 
    snap.position.y >= 0 && 
    snap.position.x + scaledWidth <= canvasSize.width && 
    snap.position.y + scaledHeight <= canvasSize.height
  );
};

/**
 * Validate image dimensions and provide recommendations
 * @param {Object} imageSize - Image dimensions {width, height}
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 * @returns {Object} Validation result with recommendations
 */
export const validateImageDimensions = (imageSize, canvasSize) => {
  const aspectRatio = imageSize.width / imageSize.height;
  const canvasAspectRatio = canvasSize.width / canvasSize.height;
  
  const validation = {
    isValid: true,
    warnings: [],
    recommendations: [],
    quality: 'good'
  };
  
  // Check resolution
  const totalPixels = imageSize.width * imageSize.height;
  if (totalPixels < 300 * 300) {
    validation.warnings.push('Image resolution is quite low');
    validation.recommendations.push('Use a higher resolution image for better quality');
    validation.quality = 'poor';
  } else if (totalPixels < 512 * 512) {
    validation.warnings.push('Image resolution could be better');
    validation.quality = 'fair';
  }
  
  // Check aspect ratio for person images (should be portrait or square)
  if (aspectRatio < 0.5) {
    validation.warnings.push('Image is very tall and narrow');
    validation.recommendations.push('Consider using a more balanced aspect ratio');
  } else if (aspectRatio > 1.5) {
    validation.warnings.push('Image is very wide');
    validation.recommendations.push('Person images work best in portrait or square format');
  }
  
  // Check if image is much larger than canvas
  const optimalScale = getOptimalScale(canvasSize, imageSize);
  if (optimalScale < 0.3) {
    validation.warnings.push('Image is much larger than canvas');
    validation.recommendations.push('Consider resizing the image before upload');
  }
  
  return validation;
};/**

 * Safely get image dimensions without causing conflicts with React components
 * @param {string} imageDataUrl - Base64 image data URL
 * @returns {Promise<Object>} Promise resolving to {width, height, success}
 */
export const getImageDimensions = (imageDataUrl) => {
  return new Promise((resolve) => {
    try {
      // Create image element in a way that avoids conflicts
      const img = document.createElement('img');
      
      img.onload = () => {
        resolve({
          width: img.naturalWidth || img.width || 0,
          height: img.naturalHeight || img.height || 0,
          success: true
        });
      };
      
      img.onerror = () => {
        resolve({
          width: 0,
          height: 0,
          success: false
        });
      };
      
      img.src = imageDataUrl;
    } catch (error) {
      console.error('Error getting image dimensions:', error);
      resolve({
        width: 0,
        height: 0,
        success: false
      });
    }
  });
};

/**
 * Get quality feedback message based on image dimensions
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {string} Quality feedback message
 */
export const getImageQualityMessage = (width, height) => {
  if (width === 0 || height === 0) {
    return 'Unable to determine image quality.';
  }
  
  const minDimension = Math.min(width, height);
  
  if (minDimension < 300) {
    return 'Image resolution is low. For best results, use images with at least 300px width/height.';
  } else if (minDimension >= 500) {
    return 'Great! This image has good resolution for virtual try-on.';
  } else {
    return 'Image quality is acceptable for virtual try-on.';
  }
};